import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription, forkJoin, interval, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { ApiService, DovsRequestListItem } from '../../shared/services/api.service';
import { AuthService } from '../../core/auth/auth.service';
import { formatDovsSubjectDisplay } from '../../shared/utils/id-number-display';

@Component({
  selector: 'app-digital-onboarding-list',
  templateUrl: './digital-onboarding-list.component.html',
  styleUrls: ['./digital-onboarding-list.component.css'],
})
export class DigitalOnboardingListComponent implements OnInit, OnDestroy {
  /** Auto-poll while any row is still waiting on XDS. */
  private static readonly AUTO_POLL_MS = 30_000;

  requests: DovsRequestListItem[] = [];
  loading = false;
  autoUpdating = false;
  refreshingId: string | null = null;
  private pollSub?: Subscription;
  private isAdmin = false;
  private autoPollInFlight = false;

  constructor(
    private api: ApiService,
    private router: Router,
    private snackBar: MatSnackBar,
    private auth: AuthService
  ) {}

  ngOnInit(): void {
    this.auth.getCurrentUser().subscribe((user) => {
      this.isAdmin = user?.role === 'admin';
    });
    this.load();
    this.pollSub = interval(DigitalOnboardingListComponent.AUTO_POLL_MS).subscribe(() => {
      void this.autoRefreshPending();
    });
  }

  ngOnDestroy(): void {
    this.pollSub?.unsubscribe();
  }

  get basePath(): string {
    return this.isAdmin ? '/admin/digital-onboarding' : '/user/digital-onboarding';
  }

  get hasPending(): boolean {
    return this.requests.some((r) => r.status === 'PENDING_VERIFICATION');
  }

  load(silent = false): void {
    if (!silent) this.loading = true;
    this.api.listDovsRequests().subscribe({
      next: (res) => {
        this.requests = res.requests || [];
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        if (!silent) {
          this.snackBar.open(err?.error?.error?.message || 'Failed to load requests', 'Close', {
            duration: 4000,
          });
        }
      },
    });
  }

  private autoRefreshPending(): void {
    if (this.autoPollInFlight || !this.hasPending) {
      return;
    }

    const pending = this.requests.filter((r) => r.status === 'PENDING_VERIFICATION');
    if (pending.length === 0) {
      return;
    }

    this.autoPollInFlight = true;
    this.autoUpdating = true;

    forkJoin(
      pending.map((row) =>
        this.api.refreshDovsRequest(row.requestId).pipe(
          catchError(() => of(null))
        )
      )
    )
      .pipe(
        finalize(() => {
          this.autoPollInFlight = false;
          this.autoUpdating = false;
        })
      )
      .subscribe((results) => {
        const newlyCompleted = results.filter((r) => r?.completed).length;
        this.load(true);
        if (newlyCompleted > 0) {
          this.snackBar.open(
            newlyCompleted === 1
              ? 'A digital onboarding verification completed'
              : `${newlyCompleted} digital onboarding verifications completed`,
            'Close',
            { duration: 4000 }
          );
        }
      });
  }

  startNew(): void {
    this.router.navigate([`${this.basePath}/new`]);
  }

  refresh(row: DovsRequestListItem): void {
    this.refreshingId = row.requestId;
    this.api.refreshDovsRequest(row.requestId).subscribe({
      next: (res) => {
        this.refreshingId = null;
        row.status = res.status;
        if (res.completed) {
          this.snackBar.open('Verification complete', 'Close', { duration: 3000 });
        } else if (res.expired) {
          this.snackBar.open('Verification expired', 'Close', { duration: 3000 });
        } else {
          this.snackBar.open('Still waiting for customer verification', 'Close', { duration: 3000 });
        }
        this.load(true);
      },
      error: (err) => {
        this.refreshingId = null;
        this.snackBar.open(err?.error?.error?.message || 'Refresh failed', 'Close', { duration: 4000 });
      },
    });
  }

  viewResults(row: DovsRequestListItem): void {
    this.router.navigate([`${this.basePath}/${row.requestId}`]);
  }

  statusLabel(status: string): string {
    switch (status) {
      case 'PENDING_VERIFICATION':
        return 'Pending verification';
      case 'COMPLETE':
        return 'Complete';
      case 'EXPIRED':
        return 'Expired';
      case 'FAILED':
        return 'Failed';
      default:
        return status;
    }
  }

  formatSubject(lookupDisplay: string | null | undefined): string {
    return formatDovsSubjectDisplay(lookupDisplay);
  }
}
